import { Icon } from '@iconify/react'
import { useEffect, useState } from 'react'
import { getChangelog } from '../api'

/** Minimal markdown → HTML renderer for changelog. */
function renderMarkdown(raw: string): string {
  return raw
    .split('\n')
    .map((line) => {
      // Unreleased header
      const unreleased = line.match(/^## \[(.+)\]/)
      if (unreleased) return `<h2 class="mt-6 mb-2 text-lg font-semibold text-amber-300">${unreleased[1]}</h2>`

      // Headings: ## Title
      const h2 = line.match(/^## (.+)/)
      if (h2) return `<h2 class="mt-6 mb-2 text-lg font-semibold text-white">${h2[1]}</h2>`

      const h3 = line.match(/^### (.+)/)
      if (h3) return `<h3 class="mt-4 mb-1 text-base font-semibold text-cyan-300">${h3[1]}</h3>`

      // List items: - text or  - `code` text
      const li = line.match(/^[-*] (.+)/)
      if (li) {
        const content = li[1].replace(/`([^`]+)`/g, '<code class="rounded bg-slate-700 px-1 text-xs text-cyan-200">$1</code>')
        return `<li class="ml-4 list-disc text-sm text-slate-300 leading-relaxed">${content}</li>`
      }

      // Empty line → spacer
      if (line.trim() === '') return ''

      // Plain paragraph
      const paragraph = line.replace(/`([^`]+)`/g, '<code class="rounded bg-slate-700 px-1 text-xs text-cyan-200">$1</code>')
      return `<p class="text-sm text-slate-400">${paragraph}</p>`
    })
    .join('\n')
}

export function ChangelogPage() {
  const [markdown, setMarkdown] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    getChangelog()
      .then((data) => {
        if (!cancelled) setMarkdown(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'fetch failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Icon icon="mdi:clipboard-list-outline" width="22" height="22" className="text-cyan-400" />
        <h1 className="text-xl font-semibold">Changelog</h1>
      </div>

      <div className="panel">
        {loading && <p className="text-sm text-slate-300">Loading...</p>}
        {!loading && error && <p className="text-sm text-rose-300">Error: {error}</p>}
        {!loading && !error && markdown && (
          <article
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
          />
        )}
        {!loading && !error && !markdown && (
          <p className="text-sm text-slate-300">No changelog entries yet.</p>
        )}
      </div>
    </div>
  )
}
